import React, { useMemo, useState } from "react"
import { Box, Typography, Chip, Tooltip, IconButton, Popover, Button } from "@mui/material"
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs"
import { LocalizationProvider, DateCalendar, PickersDay } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const HOUR_HEIGHT = 48
const START_HOUR = 6
const END_HOUR = 22
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const timeToMinutes = (t) => {
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
}

const formatTime12 = (t) => {
    const [h, m] = t.split(':').map(Number)
    const period = h >= 12 ? 'pm' : 'am'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`
}

const layoutSlots = (slots) => {
    if (!slots.length) return []
    const items = slots.map((slot, i) => ({
        index: i, start: timeToMinutes(slot.startTime), end: timeToMinutes(slot.endTime), col: 0, totalCols: 1
    })).sort((a, b) => a.start - b.start || a.end - b.end)
    const columns = []
    for (const item of items) {
        let placed = false
        for (let c = 0; c < columns.length; c++) {
            if (item.start >= columns[c]) { item.col = c; columns[c] = item.end; placed = true; break }
        }
        if (!placed) { item.col = columns.length; columns.push(item.end) }
    }
    for (const item of items) {
        const maxCol = Math.max(...items.filter(o => o.start < item.end && o.end > item.start).map(o => o.col)) + 1
        item.totalCols = maxCol
    }
    const layout = new Array(slots.length)
    for (const item of items) layout[item.index] = { col: item.col, totalCols: item.totalCols }
    return layout
}

const CHIP_HEIGHT = 18
const NAV_HEIGHT = 20

const CaseChips = ({ cases, renderCases, availableHeight, onCaseClick, slot, dateStr }) => {
    const [page, setPage] = useState(0)
    if (!cases?.length || !renderCases) return null
    const chips = renderCases(cases, { onCaseClick, slot, dateStr })
    const maxWithoutNav = Math.max(1, Math.floor(availableHeight / CHIP_HEIGHT))
    const needsNav = chips.length > maxWithoutNav
    const maxVisible = needsNav ? Math.max(1, Math.floor((availableHeight - NAV_HEIGHT) / CHIP_HEIGHT)) : maxWithoutNav
    const totalPages = Math.ceil(chips.length / maxVisible)
    const visible = totalPages > 1 ? chips.slice(page * maxVisible, (page + 1) * maxVisible) : chips

    return (
        <Box sx={{ mt: '2px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>{visible}</Box>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '1px' }}>
                    <IconButton size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                        sx={{ p: 0, color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                        <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 8, color: 'rgba(255,255,255,0.7)', mx: '2px' }}>{page + 1}/{totalPages}</Typography>
                    <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                        sx={{ p: 0, color: '#fff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                        <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    )
}

const formatDate = (d) => `${d.getMonth() + 1}/${d.getDate()}`

const getWeekDates = (baseDate) => {
    const d = new Date(baseDate); d.setHours(12, 0, 0, 0)
    const sun = new Date(d); sun.setDate(d.getDate() - d.getDay())
    return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(sun); date.setDate(sun.getDate() + i)
        return date.toISOString().split('T')[0]
    })
}

const WeekDay = ({ day, outsideCurrentMonth, weekDates, ...other }) => {
    const ds = day.format('YYYY-MM-DD')
    const inWeek = !outsideCurrentMonth && ds >= weekDates[0] && ds <= weekDates[6]
    const isStart = ds === weekDates[0]
    const isEnd = ds === weekDates[6]
    return (
        <Box sx={{
            bgcolor: inWeek ? '#90caf9' : 'transparent',
            borderRadius: isStart ? '50% 0 0 50%' : isEnd ? '0 50% 50% 0' : 0,
        }}>
            <PickersDay day={day} outsideCurrentMonth={outsideCurrentMonth} {...other}
                sx={{ bgcolor: 'transparent !important', color: inWeek ? 'rgba(0,0,0,0.87) !important' : 'inherit !important' }} />
        </Box>
    )
}

const WeekGrid = ({ schedule, templates, weekStart, onWeekChange, onSlotClick, onDayHeaderClick, onEmptyClick, onEmptyDrop, editable, renderCases, onDrop, onBulkDrop, onCaseClick, onSlotDrop, isParent, view, selectedDate }) => {
    const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart])
    const visibleDates = useMemo(() => view === 'day' && selectedDate ? [selectedDate] : weekDates, [view, selectedDate, weekDates])
    const [dragOverSlot, setDragOverSlot] = useState(null)
    const [dragOverEmpty, setDragOverEmpty] = useState(null) // { dateStr, snapTop }
    const [calAnchor, setCalAnchor] = useState(null)

    const calcSnapTop = (clientY, rect, gridH, totalMins) => {
        const y = clientY - rect.top
        const mins = Math.floor(y / gridH * totalMins) + START_HOUR * 60
        const snapped = Math.floor(mins / 30) * 30
        return ((snapped - START_HOUR * 60) / totalMins) * gridH
    }


    const dayData = useMemo(() => {
        if (!schedule?.days) return {}
        const map = {}
        for (const day of schedule.days) map[day.date] = day
        return map
    }, [schedule])

    const totalMinutes = (END_HOUR - START_HOUR) * 60
    const gridHeight = HOURS.length * HOUR_HEIGHT

    const handleDragOver = (e, slotKey) => { e.preventDefault(); setDragOverSlot(slotKey) }
    const handleDragLeave = () => setDragOverSlot(null)
    const handleDrop = (e, slot, dateStr) => {
        e.preventDefault()
        setDragOverSlot(null)
        try {
            const data = JSON.parse(e.dataTransfer.getData('text/plain'))
            if (data.studyIds && onBulkDrop) { onBulkDrop(data.studyIds, slot, dateStr); return }
            if (data.studyId && onDrop) onDrop(data.studyId, slot, dateStr)
        } catch (_) {}
    }
    const handleSlotDragStart = (e, slot, dateStr) => {
        e.stopPropagation()
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', JSON.stringify({
            slotId: String(slot._id || slot.id),
            fromDate: dateStr,
            templateId: slot.templateId || null,
            overrideId: slot.overrideId || null
        }))
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <IconButton size="small" onClick={() => { const d = new Date(view === 'day' && selectedDate ? selectedDate + 'T12:00:00' : weekStart); d.setDate(d.getDate() - (view === 'day' ? 1 : 7)); onWeekChange(d) }}><ChevronLeftIcon /></IconButton>
                <IconButton size="small" onClick={e => setCalAnchor(e.currentTarget)}><TodayIcon /></IconButton>
                <IconButton size="small" onClick={() => { const d = new Date(view === 'day' && selectedDate ? selectedDate + 'T12:00:00' : weekStart); d.setDate(d.getDate() + (view === 'day' ? 1 : 7)); onWeekChange(d) }}><ChevronRightIcon /></IconButton>
                <Popover open={!!calAnchor} anchorEl={calAnchor} onClose={() => setCalAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar
                            value={dayjs(view === 'day' && selectedDate ? selectedDate : weekDates[0])}
                            onChange={d => { if (d) { onWeekChange(d.toDate()); setCalAnchor(null) } }}
                            slots={view === 'week' ? { day: WeekDay } : undefined}
                            slotProps={view === 'week' ? { day: { weekDates } } : undefined}
                        />
                    </LocalizationProvider>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                        <Button size="small" onClick={() => { onWeekChange(new Date()); setCalAnchor(null) }}>Today</Button>
                    </Box>
                </Popover>
                <Typography variant="h6" sx={{ ml: 1 }}>
                    {view === 'day' && selectedDate
                        ? new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' })
                        : `${formatDate(new Date(weekDates[0] + 'T12:00:00'))} — ${formatDate(new Date(weekDates[6] + 'T12:00:00'))}`}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', overflow: 'hidden' }}>
                <Box sx={{ width: 50, flexShrink: 0 }}>
                    <Box sx={{ height: 52, borderBottom: 1, borderColor: 'divider' }} />
                    <Box sx={{ position: 'relative', height: gridHeight }}>
                        {HOURS.map(h => (
                            <Typography key={h} variant="caption" sx={{
                                position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT - 8, right: 4,
                                color: 'text.secondary', fontSize: 10, userSelect: 'none'
                            }}>
                                {h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
                    {visibleDates.map((dateStr, di) => {
                        const day = dayData[dateStr]
                        const dow = new Date(dateStr + 'T00:00:00Z').getUTCDay()
                        const isToday = dateStr === new Date().toISOString().split('T')[0]
                        const template = templates?.find(t => t.daysOfWeek?.includes(dow))
                        const dayMax = day?.dayMax ?? template?.dayMax
                        const totalCases = (day?.slots || []).reduce((sum, s) => sum + (s.cases?.length || 0), 0)
                        const slots = day?.slots || []
                        const slotLayout = layoutSlots(slots)

                        return (
                            <Box key={dateStr} sx={{ flex: 1, minWidth: 0, borderRight: di < visibleDates.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                                <Box onClick={() => onDayHeaderClick?.(dateStr, dow, day, template)}
                                    sx={{
                                        height: 52, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        borderBottom: 1, borderColor: 'divider',
                                        bgcolor: isToday ? 'rgba(25,118,210,0.12)' : 'transparent',
                                        cursor: onDayHeaderClick ? 'pointer' : 'default',
                                        '&:hover': onDayHeaderClick ? { bgcolor: 'action.hover' } : {}
                                    }}>
                                    <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 400, color: isToday ? 'primary.main' : 'text.primary' }}>
                                        {DAYS[dow]} {formatDate(new Date(dateStr + 'T12:00:00'))}
                                    </Typography>
                                    {(dayMax != null || template) && (
                                        <Chip size="small" label={`${totalCases}/${dayMax ?? '∞'}`}
                                            color={dayMax != null && totalCases >= dayMax ? 'error' : 'default'}
                                            sx={{ fontSize: 10, height: 16, mt: '2px' }} />
                                    )}
                                </Box>

                                <Box
                                    onClick={(e) => {
                                        if (!onEmptyClick) return
                                        if (e.target.closest('[data-slot]')) return
                                        const rect = e.currentTarget.getBoundingClientRect()
                                        const y = e.clientY - rect.top
                                        const mins = Math.floor(y / gridHeight * totalMinutes) + START_HOUR * 60
                                        const h = Math.floor(mins / 60)
                                        const m = Math.floor(mins / 30) * 30 % 60
                                        onEmptyClick(dateStr, dow, `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
                                    }}
                                    onDragOver={(onEmptyDrop || onSlotDrop) ? (e) => { if (e.target.closest('[data-slot]')) return; e.preventDefault(); const rect = e.currentTarget.getBoundingClientRect(); const clientY = e.clientY; setDragOverEmpty({ dateStr, snapTop: calcSnapTop(clientY, rect, gridHeight, totalMinutes) }) } : undefined}
                                    onDragLeave={(onEmptyDrop || onSlotDrop) ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverEmpty(null) } : undefined}
                                    onDrop={(onEmptyDrop || onSlotDrop) ? (e) => {
                                        if (e.target.closest('[data-slot]')) return
                                        e.preventDefault(); setDragOverEmpty(null)
                                        try {
                                            const data = JSON.parse(e.dataTransfer.getData('text/plain'))
                                            if (data.slotId && onSlotDrop) { onSlotDrop(data, dateStr); return }
                                            if (!data.studyId || !onEmptyDrop) return
                                            const rect = e.currentTarget.getBoundingClientRect()
                                            const y = e.clientY - rect.top
                                            const mins = Math.floor(y / gridHeight * totalMinutes) + START_HOUR * 60
                                            const h = Math.floor(mins / 60)
                                            const m = Math.floor(mins / 30) * 30 % 60
                                            const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
                                            onEmptyDrop(data.studyId, dateStr, time)
                                        } catch (_) {}
                                    } : undefined}
                                    sx={{ position: 'relative', height: gridHeight, bgcolor: dragOverEmpty?.dateStr === dateStr ? 'action.hover' : 'transparent', transition: 'background-color 0.15s' }}>
                                    {HOURS.map(h => (
                                        <Box key={h} sx={{ position: 'absolute', top: (h - START_HOUR) * HOUR_HEIGHT, left: 0, right: 0, borderTop: 1, borderColor: 'divider', pointerEvents: 'none' }} />
                                    ))}
                                    {dragOverEmpty?.dateStr === dateStr && onEmptyDrop && (
                                        <Box sx={{
                                            position: 'absolute', left: 0, right: 0,
                                            top: dragOverEmpty.snapTop - 1,
                                            height: 2, bgcolor: 'primary.main',
                                            pointerEvents: 'none', zIndex: 10,
                                            boxShadow: '0 0 4px rgba(25,118,210,0.6)'
                                        }} />
                                    )}

                                    {slots.map((slot, si) => {
                                        const startMin = timeToMinutes(slot.startTime) - START_HOUR * 60
                                        const endMin = timeToMinutes(slot.endTime) - START_HOUR * 60
                                        const top = (startMin / totalMinutes) * gridHeight
                                        const height = ((endMin - startMin) / totalMinutes) * gridHeight
                                        const { col, totalCols } = slotLayout[si] || { col: 0, totalCols: 1 }
                                        const width = `${100 / totalCols}%`
                                        const left = `${(col / totalCols) * 100}%`
                                        const orgName = isParent ? (slot.childOrg?.name || 'Unknown') : (slot.orgName || 'Unknown')
                                        const caseCount = slot.cases?.length || 0
                                        const maxLabel = slot.maxCases != null ? `/${slot.maxCases}` : '/∞'
                                        const slotKey = `${dateStr}-${slot._id || slot.id || si}`
                                        const isDragOver = dragOverSlot === slotKey

                                        return (
                                            <Tooltip key={slot._id || slot.id || si} title={slot.notes || ''} arrow placement="top">
                                                <Box data-slot
                                                    onClick={(e) => { e.stopPropagation(); onSlotClick?.(slot, day, dateStr) }}
                                                    onDragOver={(onDrop || onBulkDrop) ? (e) => { e.stopPropagation(); setDragOverEmpty(null); handleDragOver(e, slotKey) } : undefined}
                                                    onDragLeave={(onDrop || onBulkDrop) ? handleDragLeave : undefined}
                                                    onDrop={(onDrop || onBulkDrop) ? (e) => handleDrop(e, slot, dateStr) : undefined}
                                                    sx={{
                                                        position: 'absolute', top, height: Math.max(height, 20), width, left,
                                                        bgcolor: slot.color || '#1976d2', borderRadius: 1, p: '3px 5px',
                                                        overflow: 'hidden',
                                                        cursor: onSlotClick ? 'pointer' : 'default',
                                                        opacity: isDragOver ? 1 : 0.9,
                                                        '&:hover': { opacity: 1 },
                                                        boxSizing: 'border-box',
                                                        border: isDragOver ? '2px solid #fff' : '1px solid rgba(255,255,255,0.15)',
                                                        transition: 'border 0.15s, opacity 0.15s'
                                                    }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#fff', fontSize: 10, lineHeight: 1.2, display: 'block' }}>
                                                                {orgName}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 9, lineHeight: 1.2, display: 'block' }}>
                                                                {formatTime12(slot.startTime)}–{formatTime12(slot.endTime)} · {caseCount}{maxLabel}
                                                            </Typography>
                                                        </Box>
                                                        {onSlotDrop && (
                                                            <Box
                                                                draggable
                                                                onDragStart={e => handleSlotDragStart(e, slot, dateStr)}
                                                                onClick={e => e.stopPropagation()}
                                                                sx={{ cursor: 'grab', color: 'rgba(255,255,255,0.6)', fontSize: 12, pl: '4px', flexShrink: 0, lineHeight: 1, '&:hover': { color: '#fff' }, '&:active': { cursor: 'grabbing' } }}
                                                            >⠿</Box>
                                                        )}
                                                    </Box>
                                                    <CaseChips cases={slot.cases} renderCases={renderCases}
                                                        availableHeight={Math.max(height, 20) - 28}
                                                        onCaseClick={onCaseClick} slot={slot} dateStr={dateStr} />
                                                </Box>
                                            </Tooltip>
                                        )
                                    })}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}

export { DAYS_FULL }
export default WeekGrid
